import CopyButton from '@/components/ui/copy-to-clipboard/copy-button';
import { Label } from '@/components/ui/label';
import { fFullDateTime } from '@/utils/formatTime';
import { CpuIcon, RouteIcon } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import ServerlessOverviewMetrics from './metrics';
import {
  useGetServerlessEndpointById,
  useGetServerlessProjectById,
} from '@/service/serverless-gpu/hook';
import { useParams } from 'react-router-dom';
import { RouteParams } from '@/router/type';
import { fEndpointServerless } from '@/utils/formatLabel';
import BadgeActiveStatus from '../../components/BadgeActiveStatus';
import { Skeleton } from '@/components/ui/skeleton';
import TableSpotTasks from './spot/TableSpotTasks';
import { StatusServerlessProject } from '@/service/serverless-gpu/type';
import CopyIconButton from '@/components/ui/copy-to-clipboard/copy-icon-button';
import ServerlessOverviewDoc from './doc';

const ServerlessOverviewDetail: React.FC = () => {
  const { projectId } = useParams<RouteParams>();
  const { data: dataProject } = useGetServerlessProjectById(
    {
      project_id: projectId!,
    },
    {
      staleTime: 1000 * 10,
    }
  );
  const data = dataProject?.project;

  const { data: dataEndpoint, isLoading: isLoadingEndpoint } =
    useGetServerlessEndpointById(
      {
        project_id: projectId!,
      },
      {
        enabled: dataProject?.project.has_endpoint,
      }
    );

  const result = {
    deploymend: {
      endpoint: dataEndpoint?.endpoint
        ? fEndpointServerless(dataEndpoint?.endpoint).function
        : null,
      instance: data?.instance_type,
      updateAt: data?.updated_at ? fFullDateTime(data?.updated_at) : null,
      createdAt: data?.created_at ? fFullDateTime(data?.created_at) : null,
    },
  };

  const isDeployed = dataEndpoint?.endpoint;
  const isInstance = true;
  const isActive =
    dataProject?.project?.status === StatusServerlessProject.Active;
  return (
    <div>
      <div className="container">
        <div className="grid gap-8">
          {isInstance && (
            <>
              <div className="mt-4">
                <div className="mt-3 grid gap-4">
                  <div className="flex flex-wrap justify-between gap-4">
                    {/* project id */}
                    <div className="grid items-center gap-1.5">
                      <p className="text-sm text-muted-foreground">
                        Project ID
                      </p>
                      <div className="flex items-center gap-1">
                        <p className="text-sm">{data?.project_id}</p>
                        <CopyIconButton text={data?.project_id || ''} />
                      </div>
                    </div>
                    <div className="flex">
                      <div className="grid w-[150px] items-center gap-1.5 text-xs">
                        <Label className="flex gap-1 text-xs text-muted-foreground">
                          {/* <ClockIcon className="h-4 w-4" /> */}
                          Created Date
                        </Label>
                        <p className="flex items-center gap-1">
                          {result.deploymend.createdAt}
                        </p>
                      </div>
                      <div className="grid w-[150px] items-center gap-1.5 text-xs">
                        <Label className="flex gap-1 text-xs text-muted-foreground">
                          {/* <ClockIcon className="h-4 w-4" /> */}
                          Update Date
                        </Label>
                        <p className="flex items-center gap-1">
                          {result.deploymend.updateAt}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-4">
                    <div className="grid w-[150px] items-center gap-1.5">
                      <Label className="flex gap-1 text-muted-foreground">
                        <CpuIcon className="h-4 w-4" />
                        Instance
                      </Label>
                      <p className="flex items-center gap-1">
                        {result.deploymend.instance}
                      </p>
                    </div>
                    <div className="grid w-fit items-center gap-1.5">
                      <Label className="flex items-center gap-1 text-muted-foreground">
                        <RouteIcon className="h-4 w-4" />
                        Endpoint
                        {isDeployed && (
                          <BadgeActiveStatus
                            status={dataEndpoint?.is_active ?? false}
                          />
                        )}
                      </Label>
                      <p className="flex items-center gap-1">
                        {isLoadingEndpoint ? (
                          <Skeleton className="h-4 w-full" />
                        ) : result?.deploymend.endpoint ? (
                          <>
                            {result?.deploymend.endpoint}{' '}
                            <CopyButton
                              text={result?.deploymend.endpoint}
                              variant="icon"
                            />
                          </>
                        ) : (
                          '-'
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <Separator />
            </>
          )}
          {isActive && (
            <>
              {isDeployed && !isLoadingEndpoint && (
                <>
                  <ServerlessOverviewMetrics />
                  <Separator />
                </>
              )}
              <div>
                <div className="space-y-1.5">
                  <div className="text-base font-semibold">
                    Active Spot Tasks
                  </div>
                  <p className="text-sm text-muted-foreground">
                    List of spot tasks that are currently
                  </p>
                </div>
                <TableSpotTasks />
              </div>
            </>
          )}

          <Separator />
          <div>
            <ServerlessOverviewDoc />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServerlessOverviewDetail;
